import { ChevronLeft, ChevronRight, Loader2Icon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { MobileTableCard } from '~shared/components/table/mobile-table-card-list';
import { Button } from '~shared/shadcn/ui/button';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~shared/shadcn/ui/carousel';

import {
  readFamilyStudentCarouselPosition,
  scrollFamilyStudentCarousel,
} from './student-by-family-mobile-carousel.helpers';
import {
  FAMILY_STUDENT_CAROUSEL_DIRECTION,
  type StudentByFamilyMobileCarouselProps,
} from './student-by-family-mobile-carousel.types';

export function StudentByFamilyMobileCarousel<TData>({
  table,
  isUpdating,
}: StudentByFamilyMobileCarouselProps<TData>) {
  const rows = table.getRowModel().rows;
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [position, setPosition] = useState(() =>
    readFamilyStudentCarouselPosition(undefined),
  );

  const syncCarouselPosition = useCallback((api: CarouselApi) => {
    try {
      setPosition(readFamilyStudentCarouselPosition(api));
    } catch (error) {
      console.error('Failed to update the student list position', { error });
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    try {
      syncCarouselPosition(carouselApi);
      carouselApi.on('select', syncCarouselPosition);
      carouselApi.on('reInit', syncCarouselPosition);

      return () => {
        carouselApi.off('select', syncCarouselPosition);
        carouselApi.off('reInit', syncCarouselPosition);
      };
    } catch (error) {
      console.error('Failed to set up the student list', { error });
      throw error;
    }
  }, [carouselApi, syncCarouselPosition]);

  if (isUpdating && rows.length === 0) {
    return (
      <div className='flex items-center justify-center py-8 md:hidden'>
        <Loader2Icon className='size-8 animate-spin' />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground md:hidden'>
        No results.
      </p>
    );
  }

  return (
    <div className='md:hidden'>
      <Carousel
        opts={{ align: 'start', loop: false }}
        setApi={setCarouselApi}
        className='w-full'
      >
        <CarouselContent className='-ml-0'>
          {rows.map((row) => (
            <CarouselItem key={row.id} className='basis-full pl-0'>
              <MobileTableCard row={row} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className='mt-3 flex items-center justify-between gap-2'>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            className='shrink-0'
            title='Previous student'
            aria-label='Previous student'
            disabled={!position.canScrollPrev}
            onClick={() => {
              scrollFamilyStudentCarousel(
                carouselApi,
                FAMILY_STUDENT_CAROUSEL_DIRECTION.PREV,
              );
            }}
          >
            <ChevronLeft />
          </Button>
          <p className='min-w-0 text-center text-sm text-muted-foreground'>
            {position.slideCount > 0
              ? `${position.selectedIndex + 1} of ${position.slideCount}`
              : null}
          </p>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            className='shrink-0'
            title='Next student'
            aria-label='Next student'
            disabled={!position.canScrollNext}
            onClick={() => {
              scrollFamilyStudentCarousel(
                carouselApi,
                FAMILY_STUDENT_CAROUSEL_DIRECTION.NEXT,
              );
            }}
          >
            <ChevronRight />
          </Button>
        </div>
      </Carousel>
    </div>
  );
}
